import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, X, Users } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Pro-only modal: lets the user manually assign team_a / team_b for a
 * playoff match whose teams haven't auto-resolved yet (e.g. semi-final
 * displayed as "Winner of QF1" vs "Winner of QF2"). Calls updateMatch
 * which auto-derives the team_a_name / team_b_name from the team_id.
 */
export function AssignTeamModal({ open, onClose, event, match, updateMatch }) {
  const [teamAId, setTeamAId] = useState('');
  const [teamBId, setTeamBId] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset when modal opens against a different match
  React.useEffect(() => {
    if (!match) return;
    // Pre-seed if the match already has a real team_id (not a placeholder
    // like "W_<matchId>" or "Seed N"). Real team ids exist in event.teams.
    const teams = event?.teams || [];
    const realIds = new Set(teams.map(t => t.id));
    setTeamAId(realIds.has(match.team_a) ? match.team_a : '');
    setTeamBId(realIds.has(match.team_b) ? match.team_b : '');
  }, [match, event]);

  if (!open || !match) return null;
  const teams = event?.teams || [];

  const submit = async () => {
    if (!teamAId && !teamBId) {
      toast.error('Pick at least one team to assign.');
      return;
    }
    if (teamAId && teamBId && teamAId === teamBId) {
      toast.error('Teams must be different.');
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (teamAId) payload.team_a = teamAId;
      if (teamBId) payload.team_b = teamBId;
      await updateMatch(event.id, match.id, payload);
      toast.success('Match teams updated.');
      onClose();
    } catch (e) {
      toast.error(e?.message || 'Failed to update teams.');
    } finally {
      setSaving(false);
    }
  };

  const matchLabel = match.match_label
    || (match.stage ? match.stage.replace(/_/g, ' ').toUpperCase() : 'PLAYOFF');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="assign-team-modal">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto border-slate-200 shadow-xl">
        <CardHeader className="flex flex-row items-center justify-between pb-2 sticky top-0 bg-white z-10 border-b border-slate-100">
          <CardTitle className="text-xl font-bold text-[#00247D] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <Users className="w-5 h-5" /> ASSIGN TEAMS
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="assign-team-close">
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <p className="text-xs text-slate-500">
            <span className="font-semibold text-slate-700">{matchLabel}</span> —
            override the auto-populated bracket teams with the actual qualifying teams.
          </p>

          <div className="space-y-3">
            <div>
              <Label htmlFor="assign-team-a" className="text-xs font-medium text-slate-700">
                Team A
                <span className="text-slate-400 ml-2">currently: "{match.team_a_name}"</span>
              </Label>
              <Select value={teamAId} onValueChange={setTeamAId}>
                <SelectTrigger id="assign-team-a" data-testid="assign-team-a">
                  <SelectValue placeholder="Pick a team for slot A" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map(t => (
                    <SelectItem key={t.id} value={t.id} disabled={t.id === teamBId}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="assign-team-b" className="text-xs font-medium text-slate-700">
                Team B
                <span className="text-slate-400 ml-2">currently: "{match.team_b_name}"</span>
              </Label>
              <Select value={teamBId} onValueChange={setTeamBId}>
                <SelectTrigger id="assign-team-b" data-testid="assign-team-b">
                  <SelectValue placeholder="Pick a team for slot B" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map(t => (
                    <SelectItem key={t.id} value={t.id} disabled={t.id === teamAId}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="assign-team-submit">
              {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>) : 'Save Teams'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
